import { Typography } from '@mui/joy';
import { Stack } from '@mui/system';
import type { CellContext } from '@tanstack/table-core';
import { formatDate, DateTimeFormat, formatEnum } from 'components/formatter.utils';
import { IconVariant } from 'components/market/asset/cryptocurrencies/CryptocurrenciesData';
import { AccountLabel } from 'components/portfolio/account/AccountLabel';
import type { CreateSubAccountIdAutocompleteOptionsInputAccount } from 'components/portfolio/account/AccountService';
import type { GColumnDef } from 'components/technical/GTable/GTable.props';
import { DeleteMenuItem } from 'components/technical/inputs/GButton/DeleteDialogButton';
import EditDialogButton from 'components/technical/inputs/GButton/EditDialogButton';
import SeeMoreDropdown from 'components/technical/SeeMoreDropDown/SeeMoreDropdown';
import { useUserTimezone } from 'components/technical/UseUserTimezone';
import { useDeleteTransactionMutation, TransactionsDocument, TransactionFilterInputDocument } from 'generated/graphql';
import { bignumber } from 'mathjs';
import type { ReactNode, ReactElement } from 'react';
import TransactionLegSummary from '../TransactionLegSummary';
import TransactionStatusChip from '../TransactionStatusChip';
import UpdateTransactionDialogContainer from '../UpdateTransactionDialog';
import type { Transaction } from '../Transaction.types';

export const useColumns = ({
  accounts,
  existingTags,
}: {
  accounts: CreateSubAccountIdAutocompleteOptionsInputAccount[];
  existingTags: string[];
}): GColumnDef<Transaction>[] => {
  const timezone = useUserTimezone();
  const [deleteTransaction] = useDeleteTransactionMutation({
    refetchQueries: [TransactionsDocument, TransactionFilterInputDocument],
  });

  return [
    {
      id: 'accountAndTime',
      cell: (props: CellContext<Transaction, unknown>): ReactNode => {
        const { subAccount } = props.row.original;
        return (
          <Stack direction={'column'} rowGap={1}>
            <AccountLabel account={subAccount.account} size={IconVariant.XL} />
            <Typography level={'body-xs'}>
              {formatDate(props.row.original.time, DateTimeFormat.DateTime, timezone)}
            </Typography>
          </Stack>
        );
      },
    },
    {
      id: 'type',
      cell: (props: CellContext<Transaction, unknown>): ReactNode => (
        <Typography level={'title-sm'}>{formatEnum(props.row.original.userType)}</Typography>
      ),
    },
    {
      id: 'inflow',
      cell: (props: CellContext<Transaction, unknown>): ReactNode => {
        const inflows = props.row.original.legs.filter((leg) => bignumber(leg.amount).isPositive());
        return <TransactionLegSummary legs={inflows} />;
      },
    },
    {
      id: 'outflow',
      cell: (props: CellContext<Transaction, unknown>): ReactNode => {
        const outflows = props.row.original.legs.filter((leg) => bignumber(leg.amount).isNegative());
        return <TransactionLegSummary legs={outflows} />;
      },
    },
    {
      id: 'status',
      cell: (props: CellContext<Transaction, unknown>): ReactNode => {
        return <TransactionStatusChip status={props.row.original.status} />;
      },
    },
    {
      header: 'Actions',
      meta: {
        headerStyles: {
          width: '50px',
        },
        align: 'center',
      },
      cell: (props: CellContext<Transaction, unknown>): ReactNode => {
        return (
          <Stack justifyContent="center" spacing={1.5}>
            <SeeMoreDropdown>
              <EditDialogButton
                renderDialog={({ onClose }): ReactElement => (
                  <UpdateTransactionDialogContainer
                    onClose={onClose}
                    onAdded={onClose}
                    accounts={accounts}
                    existingTransaction={props.row.original}
                    existingTags={existingTags}
                  />
                )}
              />
              <DeleteMenuItem
                deletedMessage={'Transaction successfully deleted'}
                confirmationMessage={'Are you sure you want to remove transaction?'}
                deleteItem={(): Promise<unknown> => {
                  return deleteTransaction({
                    variables: {
                      input: props.row.original.id,
                    },
                  });
                }}
              />
            </SeeMoreDropdown>
          </Stack>
        );
      },
    },
  ];
};
